/**
 * Tells the reviewer which approved lines were NOT sent because Salesforce had already turned them
 * into order lines.
 *
 * ⛔ This is deliberately a blocking modal rather than a toast. The submit success path navigates
 * away after 1s and raises its toast at 4s — three seconds after this screen has unmounted — so a
 * toast here would never be seen. The navigation waits until the reviewer acknowledges.
 *
 * Why it matters that this is visible at all: the refusal is correct, but it makes the reviewer's
 * action a no-op. That is most likely to happen while they are retrying a failed order, where the
 * real recovery is a resubmit of the existing order rather than a second build — so without this
 * they would repeat an action that can never work.
 *
 * @module src/components/Inventory/InventoryQueue/InventoryDetails/AlreadyOrderedLinesModal
 */
import React, { FC } from 'react';
import Modal from '@salesforce/design-system-react/components/modal';
import Button from '@salesforce/design-system-react/components/button';
import '../index.scss';

export interface BlockedOrderLine {
  usageId?: string | null;
  usageExternalId?: string | null;
  productId?: string | null;
  code: string;
  message: string;
}

/** A blocked line joined to what the screen knows about it, so it can be named to the reviewer. */
export interface BlockedOrderLineDisplay extends BlockedOrderLine {
  productNumber?: string;
  productDescription?: string;
}

interface Props {
  isOpen: boolean;
  blockedLines: BlockedOrderLineDisplay[];
  onAcknowledge: () => void;
}

const describeLine = (line: BlockedOrderLineDisplay): string => {
  if (line.productNumber && line.productDescription) {
    return `${line.productNumber} — ${line.productDescription}`;
  }
  if (line.productNumber) {
    return line.productNumber;
  }
  if (line.productDescription) {
    return line.productDescription;
  }
  // Nothing on the screen matched the returned line. Naming it by its own identifier is still more
  // useful than an unattributed count.
  return line.usageExternalId || line.usageId || 'Unidentified line';
};

const AlreadyOrderedLinesModal: FC<Props> = ({ isOpen, blockedLines, onAcknowledge }) => {
  const headingSuffix = blockedLines.length === 1 ? 'item was' : 'items were';

  return (
    <Modal
      className="default-modal_header default-modal_footer"
      isOpen={isOpen}
      onRequestClose={onAcknowledge}
      ariaHideApp={false}
      footer={[
        <Button label="Acknowledge" variant="brand" onClick={onAcknowledge} key="acknowledge" />,
      ]}
      heading="Some items were already ordered"
    >
      <div className="slds-m-around--medium">
        <p className="slds-m-bottom--small">
          <strong>
            {blockedLines.length} approved {headingSuffix} not sent
          </strong>{' '}
          because an order already exists for them. Every other decision on this request was saved.
        </p>
        <ul className="slds-list--dotted slds-m-bottom--small">
          {blockedLines.map(line => (
            <li key={line.usageExternalId || line.usageId || line.productId || describeLine(line)}>
              {describeLine(line)}
            </li>
          ))}
        </ul>
        <p>
          Sending them again would place a second order for the same items. If this request needs to
          be corrected, work from the existing order rather than approving these lines again.
        </p>
      </div>
    </Modal>
  );
};

export default AlreadyOrderedLinesModal;
